import React, { useState, useEffect } from "react";
import { Drawer, Tag, Typography, Alert, Empty, Pagination, Spin, Button, Grid } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { answersOf, surveyTitle, feedbackKind, previewOf, ratingTone, isSubstantive } from "./feedbackItem";
import { isGuestContactQuestion } from "./guestLookup";
import GuestLookupDrawer from "./GuestLookupDrawer";
import useMaskCloseGuard from "../useMaskCloseGuard";
import { FONT } from "../../styles/typography";

/**
 * 當期回饋明細。
 *
 * 左邊清單、右邊全文，而不是把整份問卷塞進表格的一個儲存格 —— 真實問卷是
 * 24 到 29 題，塞進 330px 寬的欄位後單筆列高就超過 1100px，五筆要捲十一個螢幕，
 * 而每題都同字級平鋪，掃讀時眼睛沒有落點。
 *
 * 清單只放三件能決定「要不要點開」的事：評分、回饋類型、敘述性答案的開頭。
 *
 * 窄螢幕改成單欄鑽取：主從並列在 390px 不成立 —— 抽屜被 antd 夾到 100vw 後，
 * 清單佔掉 330px，右邊全文只剩 60px，扣掉 padding 剩 28px，而全文表格的題目欄
 * 寫死 260px，實測把答案欄壓成 0px，等於整個抽屜的用途歸零。
 *
 * 兩種用法：不帶 ids 是整期明細；帶 ids 是摘要卡某一條亮點/問題的依據下鑽，此時
 * 用 label 說明是哪一條的依據 —— 卡片上有十幾個條目，少了標題就分不出在看什麼。
 *
 * 帶了 lookupGuest 時，問卷上住客的姓名可以點，開啟該住客的住房紀錄 (GuestLookupDrawer)。
 */
export default function FeedbackItemsDrawer({ open, from, to, ids, label, onClose, fetchItems, lookupGuest, fetchGuestVisits }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // 正在查住客的那一筆問卷 id；null 表示住客抽屜關著
  const [lookupFor, setLookupFor] = useState(null);
  // xs 是 (max-width: 575px)，與 OpsDashboard 的 narrow 同一條線
  const narrow = !!Grid.useBreakpoint().xs;
  // 開啟動畫期間遮罩是暴露的，行動裝置遲到的相容性點擊會打在上面把抽屜關掉
  const maskClosable = useMaskCloseGuard(open);

  // ids 是陣列，每次 render 都是新的參照，直接放進 effect 的相依會無限重取。
  // 併成字串當作身分，換了內容才重取。
  const idsKey = ids && ids.length ? ids.join(",") : "";

  const load = (nextPage) => {
    setLoading(true);
    setError(false);
    fetchItems(from, to, undefined, nextPage, 20, ids && ids.length ? ids : undefined)
      .then((res) => {
        const rows = (res.data && res.data.data) || [];
        setItems(rows);
        setTotal(res.data ? res.data.total : 0);
        setPage(nextPage);
        setSelectedId(null);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, from, to, idsKey]);

  // 桌機沒選時退回第一筆，右邊不會開著一片空白等人點。刻意在 render 推導而不是
  // 載入完就 setSelectedId：抽屜掛載當下 antd 還沒算出斷點，那時的 narrow 一律是
  // false，寫進 state 會讓手機也自動跳進全文。
  const fallback = !narrow && items.length ? items[0] : null;
  const selected = items.find((i) => i.id === selectedId) || fallback;
  // 窄螢幕一次只給一欄：沒選任何一筆時看清單，選了就整頁看全文
  const showList = !narrow || !selected;
  const showDetail = !narrow || !!selected;
  const heading = label || "當期住客回饋明細";

  return (
    <Drawer
      // antd 4.19.5 是 visible 不是 open (open 要到 4.23)。寫錯不會報錯，
      // rc-drawer 只是永遠不打開。
      visible={open}
      onClose={onClose}
      maskClosable={maskClosable}
      // 標題在筆數還沒回來前不寫死 0 —— 抽屜是先開、資料後到，
      // 寫 0 會先閃一下「（0 則）」看起來像沒有資料
      title={total == null ? heading : `${heading}（${total} 則）`}
      // 慣例同 OrdersDrawer / RateCalendarPage：夾到 100vw 是 antd 的既有行為，寫出來讓意圖明確
      width="min(1040px, 100vw)"
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }}
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message="讀取回饋明細失敗"
          description="請稍後重試；若持續發生請通知系統管理者。"
          style={{ margin: 12 }}
        />
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {showList && (
        <div style={{
          width: narrow ? "100%" : 330,
          borderRight: narrow ? "none" : "1px solid #f0f0f0",
          overflowY: "auto",
        }}>
          {loading && <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>}
          {!loading && !items.length && !error && (
            <Empty description="本期沒有回饋" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
          )}
          {!loading &&
            items.map((item) => {
              // 比對 selected 而非 selectedId：桌機退回第一筆時 selectedId 還是 null，
              // 用 selectedId 判斷會讓右邊在看第一筆、左邊卻沒有任何一列被標起來
              const active = !!selected && item.id === selected.id;
              const kind = feedbackKind(item);
              return (
                <div
                  key={item.id}
                  // 純 div onClick 讓鍵盤使用者選不到任何一筆 (WCAG 2.1.1 Level A)。
                  // Space 要擋掉預設行為，否則會捲動抽屜而不是選取。
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(item.id)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    setSelectedId(item.id);
                  }}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f5f5f5",
                    background: active ? "#e6f7ff" : undefined,
                    borderLeft: active ? "3px solid #1890ff" : "3px solid transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    {item.rating != null ? (
                      <Tag color={ratingTone(item.rating)} style={{ marginInlineEnd: 0 }}>{item.rating} 分</Tag>
                    ) : (
                      <Tag style={{ marginInlineEnd: 0 }}>無評分</Tag>
                    )}
                    {kind && <span style={{ fontSize: FONT.caption, color: "#8c8c8c" }}>{kind}</span>}
                    <span style={{ fontSize: FONT.caption, color: "#bfbfbf", marginLeft: "auto" }}>
                      {/* 後端送的是 UTC，切字串會整整差一天 */}
                      {moment(item.occurred_at).format("MM-DD HH:mm")}
                    </span>
                  </div>
                  <div
                    style={{
                      color: "#595959",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {previewOf(item)}
                  </div>
                </div>
              );
            })}
        </div>
        )}

        {showDetail && (
        <div style={{ flex: 1, overflowY: "auto", padding: 16, minWidth: 0 }}>
          {narrow && (
            <Button
              type="link"
              size="small"
              icon={<LeftOutlined />}
              onClick={() => setSelectedId(null)}
              style={{ paddingLeft: 0, marginBottom: 4 }}
            >
              回清單
            </Button>
          )}
          {selected ? (
            <FeedbackDetail item={selected} narrow={narrow} onLookupGuest={lookupGuest ? setLookupFor : null} />
          ) : null}
        </div>
        )}
      </div>

      {/* 分頁屬於清單，窄螢幕在看全文時不該還掛著它 */}
      {showList && total != null && total > 20 && (
        <div style={{ borderTop: "1px solid #f0f0f0", padding: "8px 12px", textAlign: "right" }}>
          <Pagination size="small" current={page} pageSize={20} total={total} showSizeChanger={false} onChange={load} />
        </div>
      )}

      {lookupGuest && (
        <GuestLookupDrawer
          open={lookupFor != null}
          feedbackId={lookupFor}
          onClose={() => setLookupFor(null)}
          lookupGuest={lookupGuest}
          fetchGuestVisits={fetchGuestVisits}
        />
      )}
    </Drawer>
  );
}

const subjectStyle = { fontSize: FONT.caption, color: "#8c8c8c", wordBreak: "break-word" };

// 敘述性的答案才是要人去處理的東西，與「床墊：滿意」同字級並排就等於藏起來了
const answerStyle = (answer) => {
  const substantive = isSubstantive(answer);
  return {
    color: substantive ? "#262626" : "#8c8c8c",
    lineHeight: 1.75,
    wordBreak: "break-word",
  };
};

/**
 * 單筆全文。逐題以「標籤欄 + 內容欄」對齊，不用「題目：答案」的行內夾雜。
 *
 * 窄螢幕改成題目在上、答案在下：兩欄表格的題目欄是固定 260px，在手機寬度下
 * 會把答案欄壓到沒有寬度可分。
 */
function FeedbackDetail({ item, narrow, onLookupGuest }) {
  const answers = answersOf(item);
  const answerOf = (row) => (onLookupGuest && isGuestContactQuestion(row.subject) ? (
    <>
      <Button
        type="link"
        onClick={() => onLookupGuest(item.id)}
        style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }}
      >
        {row.answer}
      </Button>
      <span style={{ fontSize: FONT.caption, color: "#8c8c8c", marginLeft: 8 }}>點姓名查住房紀錄</span>
    </>
  ) : row.answer);
  const title = surveyTitle(item);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        {item.rating != null && <Tag color={ratingTone(item.rating)}>{item.rating} 分</Tag>}
        <Typography.Text strong>
          {title || (item.source === "google" ? "Google 評論" : "住客回饋")}
        </Typography.Text>
      </div>
      <div style={{ fontSize: FONT.caption, color: "#8c8c8c", marginBottom: 12 }}>
        {item.source === "google" ? "Google" : "SurveyCake"}
        ・{moment(item.occurred_at).format("YYYY-MM-DD HH:mm")}
        {feedbackKind(item) ? `・${feedbackKind(item)}` : ""}
      </div>

      {/* Google 評論沒有逐題結構，直接呈現全文 */}
      {!answers.length && (
        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{item.content}</div>
      )}

      {!!answers.length && narrow && (
        <div>
          {answers.map((row, i) => (
            <div key={i} style={{ borderTop: i ? "1px solid #f5f5f5" : "none", padding: "8px 0" }}>
              <div style={{ ...subjectStyle, marginBottom: 2 }}>{row.subject}</div>
              <div style={answerStyle(row.answer)}>{answerOf(row)}</div>
            </div>
          ))}
        </div>
      )}

      {!!answers.length && !narrow && (
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            {answers.map((row, i) => (
              <tr key={i} style={{ borderTop: i ? "1px solid #f5f5f5" : "none" }}>
                <td style={{ ...subjectStyle, width: 260, padding: "6px 12px 6px 0", verticalAlign: "top" }}>
                  {row.subject}
                </td>
                <td style={{ ...answerStyle(row.answer), padding: "6px 0", verticalAlign: "top" }}>
                  {answerOf(row)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
